import { Component, Prop, h, Element, State, Method } from '@stencil/core';
import { ACTION_REGISTRY, type ActionName } from '../../../actions/_generated-registry';
import type { ActionContext } from '../../../actions/types';
import { parseJsonProp } from '../../../utils/helpers/hydration';

const COMPONENT_NAME = 'me-button';

// NOTE: three icons (arrows--chevron-down, arrows--chevron-right, interface--search)
// used to be hard-coded here as inline JSX SVG "for LCP". That bypassed the fold
// policy: Stencil SSR renders every section, so each of those buttons shipped its SVG
// in the document even far below the fold (measured: 15 instances, 4.8 KB, all
// below-fold on a comparison landing). All three already exist as assets under
// source/assets/icons/, so the me-icon path below covers them — and the render
// pipeline now decides inlining per section from `metadata.isInViewport`
// (applyFoldAwareIconInlining in app/api/render/helpers/viewport-styles.ts): icons in
// the first viewport are still inlined at SSR, below-fold ones lazy-load on scroll.

/**
 * A flexible and accessible button component with support for native tags (`button`, `a`, `input`, `span`),
 * icons, and keyboard interaction.
 *
 * @slot default - {required}, {content}, string
 * @example
 *   <me-button variant="primary">Button</me-button>
 */
@Component({
  tag: 'me-button',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Button {
  @Element() host: HTMLElement;

  /**
   * Which HTML tag to render.
   * Common values: `button`, `a`, `span`, `input`.
   * @default span
   */
  @Prop() tag: 'span' | 'a' | 'button' | 'input' = 'span';

  /**
   * Button visual style variant.
   * Options: `primary` (default), `secondary`, `bordered`, `tertiary`, `detailed`, `ghost`.
   * `bordered` — white outline button for use on dark backgrounds (e.g. dark CTA sections).
   * @default primary
   */
  @Prop() variant: 'primary' | 'secondary' | 'bordered' | 'tertiary' | 'detailed' | 'ghost' = 'primary';

  /**
   * Size modifier.
   * Options: `sm`, `md`
   * @default md
   */
  @Prop() size: 'sm' | 'md' = 'md';

  /**
   * HTML `type` attribute for `<button>` or `<input>`.
   * Common values: `submit`, `button`.
   */
  @Prop() type?: 'button' | 'submit';

  /**
   * If provided, the button renders as an `<a>` with this `href`.
   */
  @Prop() href?: string;

  /**
   * Disables the button.
   * Applies `disabled` or `aria-disabled` depending on tag.
   */
  @Prop() disabled: boolean = false;

  /**
   * Native `title` attribute — shown as tooltip on hover.
   */
  @Prop() linkTitle?: string;

  /**
   * Accessibility label for screen readers.
   * Especially useful for icon-only buttons.
   */
  @Prop() ariaLabel?: string;

  /**
   * Icon name to render inside the button via `<me-icon>`.
   */
  @Prop() icon?: string;

  /**
   * Accessible alt text forwarded to `<me-icon>` for URL-based icons.
   * Required when the icon conveys meaning beyond adjacent visible text.
   */
  @Prop() iconAlt?: string;

  /**
   * Position of icon relative to text.
   * Options: `before`, `after`, `only`.
   * @default before
   */
  @Prop() iconPosition: 'before' | 'after' | 'only' = 'before';

  /**
   * Whether to preload the icon immediately (skip lazy loading).
   * Useful for icons in initially hidden elements.
   * @default false
   * @deprecated Use iconLazy="false" instead
   */
  @Prop() iconPreload?: boolean = false;

  /**
   * Enable lazy loading for the icon (default: true). Accepts boolean or string ('true'/'false').
   * When true, icons load only when near viewport. When false, loads immediately.
   * @default true
   */
  @Prop() iconLazy: boolean | string = true;

  /**
   * Inline the icon SVG during server-side rendering (SSR).
   * When true, the icon SVG is fetched from CDN on the server and inlined into HTML,
   * eliminating network requests for critical above-the-fold icons.
   * Only affects SSR - on client-side, behaves like iconLazy="false".
   * @default false
   */
  @Prop() iconInline?: boolean = false;

  /**
   * Optional native click handler.
   * Especially useful in non-framework setups.
   */
  @Prop() clickHandler?: (event: MouseEvent) => void;

  /**
   * Optional function to forward ref to inner native element.
   * Useful for modal focus traps, tooltips, etc.
   */
  @Prop() elRef?: (el: HTMLElement | null) => void;

  /**
   * Array of action names to execute on click.
   * Format: 'category:action-name'
   * Examples: ['ui:open-popup', 'nav:redirect']
   * @enumFrom ACTION_REGISTRY
   */
  @Prop({ reflect: true }) triggerActions?: ActionName[] = [];

  /**
   * Additional data to pass to actions
   */
  @Prop({ reflect: true }) actionData?: Record<string, any> = {};

  /**
   * Stores computed label (for input[type="button"])
   */
  @State() computedLabel = '';

  /**
   * True if slot has any visible content (for icon-only logic)
   */
  @State() hasSlotContent = false;

  /**
   * Specifies the target attribute for <a> tag (e.g. _blank, _self)
   */
  @Prop() target?: string;
  /**
   * Specifies the rel attribute for <a> tag (e.g. noopener, noreferrer)
   */
  @Prop() rel?: string;

  /** Ref to rendered native element */
  private renderedEl!: HTMLElement;

  /**
   * Returns the icon position (attribute takes precedence for SSR consistency).
   * When the host has a valid icon-position attribute, use it; otherwise use the prop.
   */
  private getIconPosition(): 'before' | 'after' | 'only' {
    const attr = this.host.getAttribute('icon-position');
    if (attr === 'after' || attr === 'only' || attr === 'before') return attr;
    return this.iconPosition;
  }

  /**
   * Returns the computed aria-label for the button
   */
  private getComputedAriaLabel(): string | undefined {
    if (this.ariaLabel) return this.ariaLabel;
    if (this.icon && this.getIconPosition() === 'only') {
      return this.linkTitle || this.host.textContent?.trim() || this.computedLabel;
    }
    return undefined;
  }

  /**
   * Pre-render hook: detects slot content, handles icon-only cleanup,
   * and sets computed label for input buttons.
   */
  componentWillLoad() {
    // intentionally empty — SSR wrapper cleanup moved to componentDidLoad
    // to avoid breaking Stencil's slot projection for the default slot
  }

  componentWillRender() {
    const rawText = this.host.textContent?.trim();
    this.hasSlotContent = !!rawText;

    if (this.tag === 'input') {
      this.computedLabel = rawText || '';
    }

    if (this.icon && this.getIconPosition() === 'only') {
      if (rawText) {
        this.host.innerHTML = '';
      }
      this.hasSlotContent = false;
    }
  }

  /**
   * After-render hook: adds keyboard interaction if tag is `span`.
   */
  componentDidLoad() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  componentDidUpdate() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  disconnectedCallback() {
    this.detachClickListener();
    this.host.removeEventListener('keydown', this.handleKeyDown);
  }

  private updateSpanKeyListener() {
    if (this.tag === 'span') {
      if (!this.disabled) {
        this.host.addEventListener('keydown', this.handleKeyDown);
      } else {
        this.host.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }

  /**
   * Returns the inner HTML element (<button>, <a>, etc.).
   * Can be used by parent components to control focus or attributes.
   */
  @Method()
  async getInnerElement(): Promise<HTMLElement | undefined> {
    return this.renderedEl;
  }

  /**
   * Normalized list of actions to run on click.
   * Handles both the JSON-string form (HTML attribute on server-rendered pages)
   * and the array form (property binding, e.g. Storybook / framework usage).
   */
  private getTriggerActions(): ActionName[] {
    return parseJsonProp<ActionName[]>(this.triggerActions, []) ?? [];
  }

  /**
   * Normalized action data. Handles both the JSON-string form (HTML attribute)
   * and the object form (property binding).
   */
  private getActionData(): Record<string, any> {
    return parseJsonProp<Record<string, any>>(this.actionData, {}) ?? {};
  }

  /**
   * Execute all registered actions
   */
  private async executeActions(event: MouseEvent): Promise<void> {
    const triggerActions = this.getTriggerActions();
    if (!triggerActions.length) return;

    try {
      for (const actionName of triggerActions) {
        await this.executeSingleAction(actionName, event);
      }
    } catch (error) {
      console.error('Action execution failed:', error);
    }
  }

  /**
   * Execute single action
   */
  private async executeSingleAction(actionName: ActionName, event: MouseEvent): Promise<void> {
    const actionLoader = ACTION_REGISTRY[actionName];

    if (!actionLoader) {
      console.warn(`Action "${actionName}" not found in registry`);
      return;
    }

    try {
      const actionModule = await actionLoader();
      const ActionClass = actionModule.default;
      const action = new ActionClass();

      const actionData = this.getActionData();
      const context: ActionContext = {
        event,
        element: this.renderedEl,
        component: this,
        data: actionData[actionName] ?? actionData,
      };

      await action.execute(context);
    } catch (error) {
      console.error(`Failed to execute action "${actionName}":`, error);
      throw error;
    }
  }

  /**
   * Enhanced click handler
   */
  private handleClick = async (event: MouseEvent) => {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    // Execute custom actions if any
    if (this.getTriggerActions().length) {
      await this.executeActions(event);
    }

    // Execute standard click handler if any
    if (this.clickHandler) {
      this.clickHandler(event);
    }
  };

  /**
   * Keyboard accessibility handler for <span> buttons.
   */
  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      this.host.click();
      event.preventDefault();
    }
  };

  /**
   * Computes dynamic BEM-based class list based on props and slot state.
   */
  private getClassList(): string {
    const hasIcon = !!this.icon;
    const iconPos = this.getIconPosition();
    const iconOnly = hasIcon && iconPos === 'only' && !this.hasSlotContent;
    const hostClass = this.host.getAttribute('class') ?? '';

    const classes = {
      [COMPONENT_NAME]: true,
      [`${COMPONENT_NAME}--${this.variant}`]: true,
      [`${COMPONENT_NAME}--${this.size}`]: this.size !== 'md',
      [`${COMPONENT_NAME}--disabled`]: this.disabled,
      [`${COMPONENT_NAME}--icon-${iconPos}`]: hasIcon,
      [`${COMPONENT_NAME}--icon-only`]: iconOnly,
      [hostClass]: hostClass,
    };

    return Object.entries(classes)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .join(' ');
  }

  /**
   * Generates a set of dynamic attributes depending on tag and props.
   * Handles accessibility and native tag behavior.
   */
  private getAttributes(tag: string): Record<string, string | number | boolean | EventListener | undefined> {
    const attrs = {
      'class': this.getClassList(),
      'title': this.linkTitle,
      'aria-label': this.getComputedAriaLabel(),
    } as const;

    if (tag === 'span') {
      Object.assign(attrs, {
        role: 'button',
        tabIndex: this.disabled ? -1 : 0,
        ...(this.disabled && { 'aria-disabled': 'true' }),
      });
    }

    if (tag === 'a') {
      // Always provide href for SEO - if href is not provided but tag="a" is set, use empty string
      // Empty href is better than missing href for crawlers (they can still scan the link)
      const hrefValue = this.href !== undefined && this.href !== null ? this.href : '';
      Object.assign(attrs, {
        href: hrefValue,
        target: this.target,
        rel: this.rel,
        ...(this.disabled && {
          'aria-disabled': 'true',
          'tabIndex': -1,
        }),
      });
    }

    if (tag === 'button' || tag === 'input') {
      Object.assign(attrs, { disabled: this.disabled });
    }

    if (tag === 'button' && this.type) {
      Object.assign(attrs, { type: this.type });
    }

    if (tag === 'input') {
      Object.assign(attrs, {
        type: this.type || 'button',
        value: this.computedLabel,
      });
    }

    return attrs;
  }

  /**
   * Attach click listener to the rendered element instead of inline onclick
   */
  private attachClickListener() {
    if (!this.renderedEl) return;
    this.detachClickListener();
    this.renderedEl.addEventListener('click', this.handleClick);
  }

  /**
   * Detach click listener to avoid duplicates and memory leaks
   */
  private detachClickListener() {
    if (!this.renderedEl) return;
    this.renderedEl.removeEventListener('click', this.handleClick);
  }

  /**
   * Conditionally renders the icon if provided and matches the current position.
   */
  private renderIcon(position: 'before' | 'after' | 'only') {
    if (!this.icon || this.getIconPosition() !== position) return null;

    // Every icon goes through me-icon: it lazy-loads below the fold and is inlined at
    // SSR when `inline` is set, which the render pipeline derives from the section's
    // viewport flag. See the note at the top of this file.
    return <me-icon name={this.icon} alt={this.iconAlt} class={`${COMPONENT_NAME}__icon`} isPreload={this.iconPreload} lazy={this.iconLazy} inline={this.iconInline} />;
  }

  /**
   * Main render function.
   * Dynamically renders the desired HTML tag with proper attributes and content.
   */
  render() {
    // If tag="a" is explicitly set, always render as <a> for SEO
    // Otherwise, use href to determine tag
    const Tag = this.tag === 'a' ? 'a' : this.href ? 'a' : this.tag || (this.type ? 'button' : 'span');
    const attrs = this.getAttributes(Tag);
    const isIconOnly = this.icon && this.getIconPosition() === 'only' && !this.hasSlotContent;

    return Tag === 'input' ? (
      <Tag
        ref={el => {
          this.renderedEl = el;
          this.elRef?.(el);
        }}
        {...attrs}
      />
    ) : (
      <Tag
        ref={el => {
          this.renderedEl = el;
          this.elRef?.(el);
        }}
        {...attrs}
      >
        {!isIconOnly && this.renderIcon('before')}
        {!isIconOnly && <slot />}
        {!isIconOnly && this.renderIcon('after')}
        {isIconOnly && this.renderIcon('only')}
      </Tag>
    );
  }
}
